import React, { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useLatestNews } from "../hooks/useHaber";
import "./Haberler.css";
import gsap from "gsap";

const ease = "sine.inOut";

export default function Haberler({ onActiveImageChange, onBgTransitionStart }) {
  const { latestNews: allNews, loading, error } = useLatestNews({ imageSize: "large" });
  const [filterCategory, setFilterCategory] = useState("Tümü");
  const [order, setOrder] = useState([0, 1, 2, 3, 4, 5]);
  const [detailsEven, setDetailsEven] = useState(true);
  const [animating, setAnimating] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const navigate = useNavigate();

  // Refs
  const cardRefs = useRef([]);
  const detailsEvenRef = useRef(null);
  const detailsOddRef = useRef(null);
  const progressRef = useRef(null);
  const indicatorRef = useRef(null);

  // Device detection
  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 768);
      setIsTablet(width > 768 && width <= 1024);
    };

    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  // Filter Logic
  const news = useMemo(() => {
    if (!allNews) return [];
    if (filterCategory === "Tümü") return allNews;
    return allNews.filter(item => 
      item.categories?.some(cat => (cat.title || cat.name || cat) === filterCategory)
    );
  }, [allNews, filterCategory]);

  const availableCategories = useMemo(() => {
    if (!allNews) return ["Tümü"];
    const cats = new Set(allNews.flatMap(item => item.categories?.map(c => c.title || c.name || c) || []));
    return ["Tümü", ...Array.from(cats).sort()];
  }, [allNews]);

  // Pozisyon hesaplamaları
  const { innerHeight: height, innerWidth: width } = window;
  
  // Responsive positioning
  const getPositions = () => {
    if (isMobile) {
      return {
        availableHeight: height,
        offsetTop: height - 300, // Daha yukarı al
        offsetLeft: 25, // Kenarlara yakın
        cardWidth: 220, // Daha küçük kartlar
        cardHeight: 160, // Daha küçük kartlar
        gap: 8, // Küçük boşluk
        detailsTop: height * 0.15, // Daha yukarı
        detailsLeft: 15,
        detailsWidth: width - 30
      };
    } else if (isTablet) {
      return {
        availableHeight: height,
        offsetTop: height - 350, // Daha yukarı al
        offsetLeft: height - 950, // Kenarlara yakın
        cardWidth: 360, // Orta boy kartlar
        cardHeight: 220, // Orta boy kartlar
        gap: 12, // Orta boşluk
        detailsTop: height * 0.2, // Daha yukarı
        detailsLeft: 25,
        detailsWidth: width - 50
      };
    } else {
      // Desktop - kartları büyüt
      return {
        availableHeight: height,
        offsetTop: height - 350,
        offsetLeft: width - 1100,
        cardWidth: 380,
        cardHeight: 220,
        gap: 25,
        detailsTop: 150,
        detailsLeft: 60,
        detailsWidth: 550
      };
    }
  };

  const positions = getPositions();
  const [active] = order;
  const activeNews = news && news.length > 0 ? news[active] : null;
  const bgUrl = activeNews?.cover?.url || "/images/default.jpg";

  // Navigation functions
  const goToNextNews = () => {
    if (animating || news.length <= 1) return;
    step();
  };

  const goToPrevNews = () => {
    if (animating || news.length <= 1) return;
    
    return new Promise((resolve) => {
      setAnimating(true);
      
      const newOrder = [order[order.length - 1], ...order.slice(0, -1)];
      setOrder(newOrder);
      setDetailsEven(!detailsEven);

      const detailsActive = detailsEven ? detailsOddRef.current : detailsEvenRef.current;
      const [newActiveIndex, ...newRestIndices] = newOrder;
      const previousCardIndex = newRestIndices[newRestIndices.length - 1];

      // Update content
      if (detailsActive && news[newActiveIndex]) {
        updateDetailsContent(detailsActive, news[newActiveIndex]);
      }

      // Background transition
      const nextBgUrl = news[newActiveIndex]?.cover?.url || "/images/default.jpg";
      if (onBgTransitionStart) onBgTransitionStart(nextBgUrl);

      // Animate details
      if (detailsActive) {
        const animationDuration = isMobile ? 0.4 : isTablet ? 0.5 : 0.7;
        gsap.set(detailsActive, { zIndex: 22 });
        gsap.to(detailsActive, { opacity: 1, delay: 0.3, ease });
        gsap.to(detailsActive.querySelector('.haberler-left-category'), { y: 0, delay: 0.1, duration: animationDuration, ease });
        gsap.to(detailsActive.querySelector('.haberler-left-title'), { y: 0, delay: 0.12, duration: animationDuration, ease });
        gsap.to(detailsActive.querySelector('.haberler-left-title2'), { y: 0, delay: 0.12, duration: animationDuration, ease });
        gsap.to(detailsActive.querySelector('.haberler-left-desc'), { y: 0, delay: 0.2, duration: 0.3, ease });
        gsap.to(detailsActive.querySelector('.haberler-left-cta'), { y: 0, delay: 0.25, duration: 0.3, ease });
      }

      // Card animations
      const prevCard = cardRefs.current[previousCardIndex];
      const activeCard = cardRefs.current[newActiveIndex];

      if (prevCard) {
        gsap.set(prevCard, { zIndex: 10 });
        gsap.to(prevCard, { scale: isMobile ? 1.1 : isTablet ? 1.2 : 1.5, ease });
      }

      if (activeCard) {
        // Gelen kartı önce ilk önizleme yuvasına al (uzaktaki/son kart tüm ekranı kat
        // etmesin -> "saçma geçiş" gider), sonra tam ekrana büyüt. İleri geçişte kart
        // zaten bu yuvada olduğundan görünüm değişmez; tutarlı.
        gsap.set(activeCard, {
          x: positions.offsetLeft,
          y: positions.offsetTop,
          width: positions.cardWidth,
          height: positions.cardHeight,
          scale: 1,
          opacity: 1,
          zIndex: 20,
        });
        gsap.to(activeCard, {
          x: 0,
          y: 0,
          width: window.innerWidth,
          height: positions.availableHeight,
          borderRadius: 0,
          ease,
          onComplete: () => {
            // Reset previous card to correct position
            if (prevCard) {
              if (isMobile || isTablet) {
                const newRestCards = newRestIndices.slice(0, 3);
                const prevCardIndex = newRestCards.findIndex(idx => idx === previousCardIndex);
                
                if (prevCardIndex !== -1) {
                  const isLastCard = prevCardIndex === 2;
                  const cardWidth = isLastCard ? positions.cardWidth * 0.7 : positions.cardWidth;
                  const xPosition = positions.offsetLeft + prevCardIndex * (positions.cardWidth + positions.gap);
                  
                  gsap.set(prevCard, {
                    x: xPosition,
                    y: positions.offsetTop,
                    width: cardWidth,
                    height: positions.cardHeight,
                    zIndex: 30,
                    borderRadius: isMobile ? 6 : 8,
                    scale: 1,
                    opacity: 1,
                  });
                } else {
                  gsap.set(prevCard, {
                    x: -2000,
                    y: -2000,
                    opacity: 0,
                    zIndex: 1,
                    scale: 1,
                  });
                }
              } else {
                const newRestVisible = newRestIndices.slice(0, 5);
                const prevCardNewIndex = newRestVisible.findIndex(idx => idx === previousCardIndex);
                
                if (prevCardNewIndex !== -1) {
                  const xNew = positions.offsetLeft + prevCardNewIndex * (positions.cardWidth + positions.gap);
                  gsap.set(prevCard, {
                    x: xNew,
                    y: positions.offsetTop,
                    width: positions.cardWidth,
                    height: positions.cardHeight,
                    zIndex: 30,
                    borderRadius: 12,
                    scale: 1,
                  });
                }
              }
            }

            // Update other visible cards
            if (isMobile || isTablet) {
              const newRestVisible = newRestIndices.slice(0, 3);
              
              newRestVisible.forEach((newsIndex, index) => {
                if (newsIndex !== previousCardIndex) {
                  const card = cardRefs.current[newsIndex];
                  if (card) {
                    const isLastCard = index === 2;
                    const cardWidth = isLastCard ? positions.cardWidth * 0.7 : positions.cardWidth;
                    const xPosition = positions.offsetLeft + index * (positions.cardWidth + positions.gap);
                    
                    gsap.set(card, { zIndex: 30, opacity: 1 });
                    gsap.to(card, {
                      x: xPosition,
                      y: positions.offsetTop,
                      width: cardWidth,
                      height: positions.cardHeight,
                      borderRadius: isMobile ? 6 : 8,
                      ease,
                      delay: 0.05 * (index + 1),
                    });
                  }
                }
              });

              newRestIndices.slice(3).forEach((newsIndex) => {
                if (newsIndex !== previousCardIndex) {
                  const card = cardRefs.current[newsIndex];
                  if (card) {
                    gsap.set(card, { zIndex: 1 });
                    gsap.to(card, {
                      x: -2000,
                      y: -2000,
                      opacity: 0,
                      ease,
                    });
                  }
                }
              });
            } else {
              const newRestVisible = newRestIndices.slice(0, 5);
              
              newRestVisible.forEach((newsIndex, index) => {
                if (newsIndex !== previousCardIndex) {
                  const card = cardRefs.current[newsIndex];
                  if (card) {
                    const xNew = positions.offsetLeft + index * (positions.cardWidth + positions.gap);
                    gsap.set(card, { zIndex: 30 });
                    gsap.to(card, {
                      x: xNew,
                      y: positions.offsetTop,
                      width: positions.cardWidth,
                      height: positions.cardHeight,
                      ease,
                      delay: 0.1 * (index + 1),
                    });
                  }
                }
              });
            }

            updateProgress(newActiveIndex);
            setAnimating(false);
            resolve();
          },
        });
      }
    });
  };

  const updateDetailsContent = (detailsElement, newsItem) => {
    const categoryEl = detailsElement.querySelector('.haberler-left-category');
    const titleEl = detailsElement.querySelector('.haberler-left-title');
    const title2El = detailsElement.querySelector('.haberler-left-title2');
    const descEl = detailsElement.querySelector('.haberler-left-desc');

    if (categoryEl) categoryEl.textContent = newsItem.categories?.[0]?.title || "Haber";
    if (titleEl) titleEl.textContent = newsItem.title?.split(" ").slice(0, 2).join(" ") || "";
    if (title2El) title2El.textContent = newsItem.title?.split(" ").slice(2).join(" ") || "";
    if (descEl) descEl.textContent = newsItem.description || "";
  };

  const updateProgress = (activeIndex) => {
    if (progressRef.current) {
      const progressWidth = isMobile ? 140 : isTablet ? 180 : 500;
      // Her slayt için progress bar'ın 1/6'sı doldurulmalı (6 haber olduğu için)
      const fillPercentage = ((activeIndex + 1) / order.length);
      const fillWidth = progressWidth * fillPercentage;
      
      gsap.to(progressRef.current, {
        width: fillWidth + "px",
        ease,
        duration: 0.5
      });
    }
  };

  const goToNewsDetail = () => {
    if (activeNews?.slug) {
      navigate(`/haberler/${activeNews.slug}`);
    }
  };

  // Initialize function - responsive.
  // ord: taze order'ı dışarıdan geçmek için (init effect setOrder çağırınca bu fonksiyonun
  // closure'ı hâlâ eski order'ı gördüğünden küçük kartlar order.slice(1,6)=[] ile yerleşmiyordu).
  const initializeCards = (ord = order) => {
    if (!news || news.length === 0 || initialized) return;

    const detailsActive = detailsEven ? detailsEvenRef.current : detailsOddRef.current;
    const detailsInactive = detailsEven ? detailsOddRef.current : detailsEvenRef.current;

    // Ana kartı (ilk sıradaki) tam ekran yap
    const activeCardIndex = ord[0];
    const activeCard = cardRefs.current[activeCardIndex];
    if (activeCard) {
      gsap.set(activeCard, {
        x: 0,
        y: 0,
        width: window.innerWidth,
        height: positions.availableHeight,
        zIndex: 20,
        borderRadius: 0,
      });
    }

    // Diğer kartları responsive konumlarda yerleştir
    if (isMobile || isTablet) {
      // Mobil/Tablet: Sadece 3 kart göster (2 tam + 1 yarım)
      const restCards = ord.slice(1, 4); // Sadece ilk 3 kartı al
      
      restCards.forEach((newsIndex, index) => {
        const card = cardRefs.current[newsIndex];
        if (card) {
          // Son kart (index 2) yarım genişlik
          const isLastCard = index === 2;
          const cardWidth = isLastCard ? positions.cardWidth * 0.7 : positions.cardWidth;
          
          gsap.set(card, {
            x: positions.offsetLeft + index * (positions.cardWidth + positions.gap),
            y: positions.offsetTop,
            width: cardWidth,
            height: positions.cardHeight,
            zIndex: 30,
            borderRadius: isMobile ? 6 : 8,
          });
        }
      });

      // Geri kalan kartları tamamen gizle
      ord.slice(4).forEach((newsIndex) => {
        const card = cardRefs.current[newsIndex];
        if (card) {
          gsap.set(card, {
            x: -2000, // Tamamen ekran dışı
            y: -2000,
            opacity: 0,
            zIndex: 1,
          });
        }
      });
    } else {
      // Desktop: 5 kart göster
      ord.slice(1, 6).forEach((newsIndex, index) => {
        const card = cardRefs.current[newsIndex];
        if (card) {
          gsap.set(card, {
            x: positions.offsetLeft + index * (positions.cardWidth + positions.gap),
            y: positions.offsetTop,
            width: positions.cardWidth,
            height: positions.cardHeight,
            zIndex: 30,
            borderRadius: 12,
          });
        }
      });
    }

    // Progress bar
    updateProgress(0);

    // Details ayarları - responsive
    if (detailsActive) {
      gsap.set(detailsActive, { opacity: 1, x: 0, zIndex: 22 });
    }
    if (detailsInactive) {
      gsap.set(detailsInactive, { opacity: 0, zIndex: 12 });
    }

    setInitialized(true);
  };

  // Step function - responsive.
  // k: kaç adım ilerleneceği (varsayılan 1 = sıradaki). Önizleme kartına tıklamada
  // o kartın order'daki konumu (k) geçilir -> tıklanan kart tek geçişle ana ekrana gelir.
  const step = (k = 1) => {
    if (news.length <= 1) return Promise.resolve();

    return new Promise((resolve) => {
      const newOrder = [...order.slice(k), ...order.slice(0, k)];
      setOrder(newOrder);
      setDetailsEven(!detailsEven);

      const detailsActive = detailsEven ? detailsOddRef.current : detailsEvenRef.current;
      const [newActiveIndex, ...newRestIndices] = newOrder;
      const previousCardIndex = order[0]; // eski ana kart (tam ekrandan önizlemeye döner)

      // Update content
      if (detailsActive && news[newActiveIndex]) {
        updateDetailsContent(detailsActive, news[newActiveIndex]);
      }

      // Background transition - fix URL access
      const nextBgUrl = news[newActiveIndex]?.cover?.url || "/images/default.jpg";
      if (onBgTransitionStart) onBgTransitionStart(nextBgUrl);

      // Animate details - responsive timing
      if (detailsActive) {
        const animationDuration = isMobile ? 0.4 : isTablet ? 0.5 : 0.7;
        gsap.set(detailsActive, { zIndex: 22 });
        gsap.to(detailsActive, { opacity: 1, delay: 0.3, ease });
        gsap.to(detailsActive.querySelector('.haberler-left-category'), { y: 0, delay: 0.1, duration: animationDuration, ease });
        gsap.to(detailsActive.querySelector('.haberler-left-title'), { y: 0, delay: 0.12, duration: animationDuration, ease });
        gsap.to(detailsActive.querySelector('.haberler-left-title2'), { y: 0, delay: 0.12, duration: animationDuration, ease });
        gsap.to(detailsActive.querySelector('.haberler-left-desc'), { y: 0, delay: 0.2, duration: 0.3, ease });
        gsap.to(detailsActive.querySelector('.haberler-left-cta'), { y: 0, delay: 0.25, duration: 0.3, ease });
      }

      // Card animations
      const prevCard = cardRefs.current[previousCardIndex];
      const activeCard = cardRefs.current[newActiveIndex];

      if (prevCard) {
        gsap.set(prevCard, { zIndex: 10 });
        gsap.to(prevCard, { scale: isMobile ? 1.1 : isTablet ? 1.2 : 1.5, ease });
      }

      if (activeCard) {
        // Gelen kartı önce ilk önizleme yuvasına al (uzaktaki/son kart tüm ekranı kat
        // etmesin -> "saçma geçiş" gider), sonra tam ekrana büyüt. İleri geçişte kart
        // zaten bu yuvada olduğundan görünüm değişmez; tutarlı.
        gsap.set(activeCard, {
          x: positions.offsetLeft,
          y: positions.offsetTop,
          width: positions.cardWidth,
          height: positions.cardHeight,
          scale: 1,
          opacity: 1,
          zIndex: 20,
        });
        gsap.to(activeCard, {
          x: 0,
          y: 0,
          width: window.innerWidth,
          height: positions.availableHeight,
          borderRadius: 0,
          ease,
          onComplete: () => {
            // Reset previous card to correct position
            if (prevCard) {
              if (isMobile || isTablet) {
                // Mobil/Tablet için 3 kart düzeni
                const newRestCards = newRestIndices.slice(0, 3);
                const prevCardIndex = newRestCards.findIndex(idx => idx === previousCardIndex);
                
                if (prevCardIndex !== -1) {
                  const isLastCard = prevCardIndex === 2;
                  const cardWidth = isLastCard ? positions.cardWidth * 0.7 : positions.cardWidth;
                  const xPosition = positions.offsetLeft + prevCardIndex * (positions.cardWidth + positions.gap);
                  
                  gsap.set(prevCard, {
                    x: xPosition,
                    y: positions.offsetTop,
                    width: cardWidth,
                    height: positions.cardHeight,
                    zIndex: 30,
                    borderRadius: isMobile ? 6 : 8,
                    scale: 1,
                    opacity: 1,
                  });
                } else {
                  // Görünür kartlar arasında değilse tamamen gizle
                  gsap.set(prevCard, {
                    x: -2000,
                    y: -2000,
                    opacity: 0,
                    zIndex: 1,
                    scale: 1,
                  });
                }
              } else {
                // Desktop için normal düzen
                const newRestVisible = newRestIndices.slice(0, 5);
                const prevCardNewIndex = newRestVisible.findIndex(idx => idx === previousCardIndex);
                
                if (prevCardNewIndex !== -1) {
                  const xNew = positions.offsetLeft + prevCardNewIndex * (positions.cardWidth + positions.gap);
                  gsap.set(prevCard, {
                    x: xNew,
                    y: positions.offsetTop,
                    width: positions.cardWidth,
                    height: positions.cardHeight,
                    zIndex: 30,
                    borderRadius: 12,
                    scale: 1,
                  });
                }
              }
            }

            // Update other visible cards
            if (isMobile || isTablet) {
              // Mobil/Tablet: 3 kart düzeni
              const newRestVisible = newRestIndices.slice(0, 3);
              
              newRestVisible.forEach((newsIndex, index) => {
                if (newsIndex !== previousCardIndex) {
                  const card = cardRefs.current[newsIndex];
                  if (card) {
                    const isLastCard = index === 2;
                    const cardWidth = isLastCard ? positions.cardWidth * 0.7 : positions.cardWidth;
                    const xPosition = positions.offsetLeft + index * (positions.cardWidth + positions.gap);
                    
                    gsap.set(card, { zIndex: 30, opacity: 1 });
                    gsap.to(card, {
                      x: xPosition,
                      y: positions.offsetTop,
                      width: cardWidth,
                      height: positions.cardHeight,
                      borderRadius: isMobile ? 6 : 8,
                      ease,
                      delay: 0.05 * (index + 1),
                    });
                  }
                }
              });

              // Geri kalan kartları gizle
              newRestIndices.slice(3).forEach((newsIndex) => {
                if (newsIndex !== previousCardIndex) {
                  const card = cardRefs.current[newsIndex];
                  if (card) {
                    gsap.set(card, { zIndex: 1 });
                    gsap.to(card, {
                      x: -2000,
                      y: -2000,
                      opacity: 0,
                      ease,
                    });
                  }
                }
              });
            } else {
              // Desktop: 5 kart düzeni
              const newRestVisible = newRestIndices.slice(0, 5);
              
              newRestVisible.forEach((newsIndex, index) => {
                if (newsIndex !== previousCardIndex) {
                  const card = cardRefs.current[newsIndex];
                  if (card) {
                    const xNew = positions.offsetLeft + index * (positions.cardWidth + positions.gap);
                    gsap.set(card, { zIndex: 30 });
                    gsap.to(card, {
                      x: xNew,
                      y: positions.offsetTop,
                      width: positions.cardWidth,
                      height: positions.cardHeight,
                      ease,
                      delay: 0.1 * (index + 1),
                    });
                  }
                }
              });
            }

            // Progress bar'ı yeni aktif haber için güncelle
            updateProgress(newActiveIndex);
            resolve();
          },
        });
      }
    });
  };

  // Önizleme kartına tıklayınca o haberi aynı geçiş efektiyle ana ekrana getir
  const goToCard = (targetNewsIndex) => {
    if (animating || news.length <= 1) return;
    const k = order.indexOf(targetNewsIndex);
    if (k < 1) return; // yalnızca önizleme kartları (ana kart k=0 -> "Haberi Gör" butonu var)
    setAnimating(true);
    Promise.resolve(step(k)).finally(() => setAnimating(false));
  };

  // Initialize when news loads or changes (filtering)
  useEffect(() => {
    if (news && news.length > 0 && !initialized) {
      const newOrder = news.slice(0, 6).map((_, i) => i);
      setOrder(newOrder);
      setTimeout(() => initializeCards(newOrder), 100);
    }
  }, [news, initialized, isMobile, isTablet, filterCategory]);

  // Reinitialize on device change
  useEffect(() => {
    if (initialized) {
      setInitialized(false);
      setTimeout(() => initializeCards(), 100);
    }
  }, [isMobile, isTablet]);

  // Pencere GENİŞLİĞİ değişince kartları yeniden konumlandır ("kayık" hizalama fix).
  // Kart x konumu window genişliğine bağlı (offsetLeft = width - 1100); aynı cihaz
  // kategorisinde genişlik değişince yeniden yerleşmiyordu. setInitialized(false) ->
  // yukarıdaki init effect kartları yeni genişliğe göre tekrar yerleştirir.
  useEffect(() => {
    let t;
    const onResize = () => {
      clearTimeout(t);
      t = setTimeout(() => setInitialized(false), 180);
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);

  // Reset initialization when filter changes
  useEffect(() => {
    setInitialized(false);
    setOrder([0]); // Temporary reset
  }, [filterCategory]);

  // Update background when active changes
  useEffect(() => {
    if (onActiveImageChange && activeNews) {
      onActiveImageChange(bgUrl);
    }
  }, [active, activeNews, onActiveImageChange, bgUrl]);

  // Ana kart geçişlerinde orijinal görsel hazır olsun diye tüm haber orijinallerini
  // arka planda (idle) önyükle -> bir kart ana ekrana gelince small->orijinal glitch olmaz.
  useEffect(() => {
    if (!news || news.length === 0) return;
    let id;
    const preload = () => {
      news.slice(0, 6).forEach((n) => {
        const u = n?.cover?.url;
        if (u) { const img = new Image(); img.decoding = "async"; img.src = u; }
      });
    };
    if ("requestIdleCallback" in window) {
      id = window.requestIdleCallback(preload, { timeout: 2500 });
    } else {
      id = setTimeout(preload, 1200);
    }
    return () => {
      if ("cancelIdleCallback" in window && typeof id === "number" && window.cancelIdleCallback) window.cancelIdleCallback(id);
      else clearTimeout(id);
    };
  }, [news]);

  // Auto loop - responsive timing
  useEffect(() => {
    if (!initialized || !news || news.length <= 1 || animating) return;

    const loop = async () => {
      // Indicator animation
      if (indicatorRef.current) {
        gsap.set(indicatorRef.current, { x: -window.innerWidth });
        const indicatorDuration = isMobile ? 1.5 : isTablet ? 1.7 : 2;
        await gsap.to(indicatorRef.current, { x: 0, duration: indicatorDuration }).then();
        await gsap.to(indicatorRef.current, { x: window.innerWidth, duration: 0.8, delay: 0.3 }).then();
      }

      setAnimating(true);
      await step();
      setAnimating(false);
    };

    const loopDuration = isMobile ? 4000 : isTablet ? 5000 : 6000;
    const timeout = setTimeout(loop, loopDuration);
    return () => clearTimeout(timeout);
  }, [initialized, animating, active, news, order, isMobile, isTablet]);

  if (loading) {
    return (
      <div className="haberler-loading-container">
        <div className="haberler-loading-box">
          <div className="haberler-loading-spinner" />
          <span>Haberler yükleniyor...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="haberler-loading-container">
        <div className="haberler-loading-box">
          <span style={{ fontSize: "2rem" }}>⚠️</span>
          <span style={{ color: "#ef4444" }}>Haberler yüklenemedi</span>
        </div>
      </div>
    );
  }

  if (!loading && (!news || news.length === 0 || !activeNews)) {
    return (
      <div className="haberler-loading-container">
        <div className="haberler-loading-box">
          <span style={{ fontSize: "2rem" }}>📭</span>
          <span>Hiç haber bulunamadı.</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`haberler-gallery-root ${isMobile ? 'haberler-mobile' : isTablet ? 'haberler-tablet' : 'haberler-desktop'}`}>
      {/* Indicator */}
      <div ref={indicatorRef} className="indicator" />

      {/* Filter Bar */}
      <div className="haberler-filter-bar">
        {availableCategories.map(cat => (
          <button
            key={cat}
            className={`haberler-filter-btn ${filterCategory === cat ? 'active' : ''}`}
            onClick={() => setFilterCategory(cat)}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Cards */}
      <div id="demo">
        {news.slice(0, 6).map((newsItem, newsIndex) => (
          <div
            key={newsItem?.id || newsIndex}
            ref={el => cardRefs.current[newsIndex] = el}
            className="card"
            onClick={() => goToCard(newsIndex)}
            style={{
              cursor: order[0] === newsIndex ? "default" : "pointer",
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              backgroundImage: `url(${
                order[0] === newsIndex
                  ? (newsItem?.cover?.url || "/images/default.jpg")
                  : (newsItem?.cover?.formats?.small || newsItem?.cover?.formats?.thumbnail || newsItem?.cover?.url || "/images/default.jpg")
              })`,
              backgroundPosition: "center",
              backgroundSize: "cover",
              boxShadow: "6px 6px 10px 2px rgba(0, 0, 0, 0.6)",
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "flex-start",
              padding: order[0] === newsIndex ? "0" : (isMobile ? "8px" : isTablet ? "12px" : "16px"),
              color: "white",
            }}
          >
            {/* Ana kart için karartı overlay */}
            {order[0] === newsIndex && (
              <div className="card-overlay" />
            )}

            {/* Küçük kartlarda başlık */}
            {order[0] !== newsIndex && (
              <div className="card-small-title">
                <h2 className="card-small-title-text">
                  {newsItem?.title || "Haber Başlığı"}
                </h2>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Details Even */}
      <div
        ref={detailsEvenRef}
        className={`details haberler-left ${isMobile ? 'haberler-left-mobile' : isTablet ? 'haberler-left-tablet' : ''}`}
        style={{
          zIndex: detailsEven ? 22 : 12,
          position: "absolute",
          left: positions.detailsLeft + "px",
          top: positions.detailsTop + "px",
          color: "#fff",
          opacity: detailsEven ? 1 : 0,
          width: positions.detailsWidth + "px"
        }}
      >
        <div className="haberler-left-indicator">
          <div className="haberler-left-indicator-bar" />
        </div>
        <div className="haberler-left-category">
          {activeNews?.categories?.[0]?.title || "Haber"}
        </div>
        <div className="haberler-left-title">
          {activeNews?.title?.split(" ").slice(0, 2).join(" ")}
        </div>
        <div className="haberler-left-title2">
          {activeNews?.title?.split(" ").slice(2).join(" ")}
        </div>
        <div className={`haberler-left-desc${isMobile ? " haberler-left-desc-hide-mobile" : ""}`}>
          {activeNews?.description || ""}
        </div>
        <div className="haberler-left-cta">
          <button onClick={goToNewsDetail} className="haberler-left-discover">
            Haberi Gör
          </button>
        </div>
      </div>

      {/* Details Odd */}
      <div
        ref={detailsOddRef}
        className={`details haberler-left ${isMobile ? 'haberler-left-mobile' : isTablet ? 'haberler-left-tablet' : ''}`}
        style={{
          zIndex: !detailsEven ? 22 : 12,
          position: "absolute",
          left: positions.detailsLeft + "px",
          top: positions.detailsTop + "px",
          color: "#fff",
          opacity: !detailsEven ? 1 : 0,
          width: positions.detailsWidth + "px"
        }}
      >
        <div className="haberler-left-indicator">
          <div className="haberler-left-indicator-bar" />
        </div>
        <div className="haberler-left-category">
          {activeNews?.categories?.[0]?.title || "Haber"}
        </div>
        <div className="haberler-left-title">
          {activeNews?.title?.split(" ").slice(0, 2).join(" ")}
        </div>
        <div className="haberler-left-title2">
          {activeNews?.title?.split(" ").slice(2).join(" ")}
        </div>
        <div className={`haberler-left-desc${isMobile ? " haberler-left-desc-hide-mobile" : ""}`}>
          {activeNews?.description || ""}
        </div>
        <div className="haberler-left-cta">
          <button onClick={goToNewsDetail} className="haberler-left-discover">
            Haberi Gör
          </button>
        </div>
      </div>

      {/* Pagination - responsive positioning */}
      {news.length > 1 && (
        <div 
          className={`pagination ${isMobile ? 'pagination-mobile' : isTablet ? 'pagination-tablet' : ''}`}
          style={{ 
            position: "absolute", 
            left: positions.offsetLeft + "px", 
            top: (positions.offsetTop + positions.cardHeight + (isMobile ? 15 : isTablet ? 20 : 30)) + "px", 
            zIndex: 60, 
            display: "inline-flex" 
          }}
        >
          <button type="button" onClick={goToPrevNews} className="arrow arrow-left" aria-label="Önceki haber">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
            </svg>
          </button>
          <button type="button" onClick={goToNextNews} className="arrow arrow-right" aria-label="Sonraki haber">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
            </svg>
          </button>
          <div className="progress-sub-container">
            <div className="progress-sub-background">
              <div ref={progressRef} className="progress-sub-foreground"></div>
            </div>
          </div>
          <div className="slide-numbers">
            <div className="item">{active + 1}</div>
          </div>
        </div>
      )}
    </div>
  );
}